import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetCartQuery } from "../../cart/api/cartApi";
import { useGetCheckoutDataQuery } from "../api/checkoutApi";
import {
  generateAgreementHtml,
  AgreementLabels,
} from "../helpers/generateAgreementHtml.ts";

/**
 * A custom hook for the Legal Agreements feature.
 * Handles fetching necessary data (Cart, Checkout) and providing
 * a context-aware HTML generation function.
 */
export const useLegalAgreements = () => {
  const { t } = useTranslation();
  const { data: cart, isLoading: isCartLoading } = useGetCartQuery();
  const { data: checkoutData, isLoading: isCheckoutLoading } =
    useGetCheckoutDataQuery();

  // Prepare localized labels for the HTML generator
  const labels: AgreementLabels = useMemo(
    () => ({
      customerType: t(
        "legalAgreements.generatedContent.customerType",
        "Customer Type"
      ),
      individual: t(
        "legalAgreements.generatedContent.individual",
        "Individual"
      ),
      corporate: t("legalAgreements.generatedContent.corporate", "Corporate"),
      companyTitle: t(
        "legalAgreements.generatedContent.companyTitle",
        "Company Title"
      ),
      tckn: t("legalAgreements.generatedContent.tckn", "TC Identity No"),
      vkn: t("legalAgreements.generatedContent.vkn", "Tax ID No"),
      notSpecified: t(
        "legalAgreements.generatedContent.notSpecified",
        "Not Specified"
      ),
      authorized: t(
        "legalAgreements.generatedContent.authorized",
        "Authorized"
      ),
      orderNumberPlaceholder: t(
        "legalAgreements.generatedContent.orderNumberPlaceholder",
        "(Generating...)"
      ),
      paymentMethod: t("orderPage.summary.paymentMethod", "Payment Method"),
      table: {
        product: t("legalAgreements.generatedContent.table.product", "Product"),
        quantity: t("legalAgreements.generatedContent.table.quantity", "Qty"),
        total: t("legalAgreements.generatedContent.table.total", "Total"),
        subtotal: t(
          "legalAgreements.generatedContent.table.subtotal",
          "Subtotal"
        ),
        discount: t(
          "legalAgreements.generatedContent.table.discount",
          "Discount"
        ),
        shipping: t(
          "legalAgreements.generatedContent.table.shipping",
          "Shipping"
        ),
        fees: t("legalAgreements.generatedContent.table.fees", "Fees"),
        tax: t("legalAgreements.generatedContent.table.tax", "Tax"),
        grandTotal: t(
          "legalAgreements.generatedContent.table.grandTotal",
          "GRAND TOTAL"
        ),
        freeShipping: t(
          "legalAgreements.generatedContent.table.freeShipping",
          "Free Shipping"
        ),
        localPickup: t(
          "legalAgreements.generatedContent.table.localPickup",
          "Local Pickup"
        ),
        paymentMethod: t("orderPage.summary.paymentMethod", "Payment Method"),
      },
    }),
    [t]
  );

  /**
   * Generates the final HTML for a specific agreement template.
   */
  const generateHtml = useCallback(
    (template: string) => {
      if (!cart || !checkoutData) return "";
      return generateAgreementHtml(template, cart, checkoutData, labels);
    },
    [cart, checkoutData, labels]
  );

  return {
    generateHtml,
    isLoading: isCartLoading || isCheckoutLoading,
    isReady: !!cart && !!checkoutData,
    templates: checkoutData?.legalAgreements?.templates,
  };
};
